import { lazy, Suspense, useState, useEffect } from 'react';
import { ErrorBoundary } from '@sentry/react';
import { BrowserRouter, Routes, Route, Outlet, useSearchParams } from 'react-router-dom';
import { ROUTES } from './routes';
import RootLayout from './ui/layouts/RootLayout';
import AuthenticatedLayout from './ui/layouts/AuthenticatedLayout';
import AuthenticatedNavbarLayout from './ui/layouts/AuthenticatedNavbarLayout';
import legalLayoutStyles from './ui/layouts/LegalLayout.module.css';
import { useLayoutVariant } from '@breath-lab/state';
import { useAuth } from '@breath-lab/state';
import { useSubscription, useTrialState, useTrialStartedEvent } from '@breath-lab/state';
import ProtectedRoute from '@ui/components/ProtectedRoute/ProtectedRoute';
import ScrollToTop from '@ui/components/ScrollToTop/ScrollToTop';
import CookieBanner from '@ui/components/CookieBanner/CookieBanner';
import { getConsent, acceptAll, acceptEssential, type CookieConsent } from './domain/cookieConsent';
import { setUserProperty, applyAnalyticsConsent } from './infra/firebase/analytics';
import { AnalyticsUserProperty } from './domain/analytics';
import { buildAnalyticsUserProperties } from '@breath-lab/domain';

const WEB_VERSION = import.meta.env.VITE_APP_VERSION as string;

const LandingPage = lazy(() => import('./ui/pages/LandingPage/LandingPage'));
const HomePage = lazy(() => import('./ui/pages/HomePage/HomePage'));
const ActivityPage = lazy(() => import('./ui/pages/ActivityPage/ActivityPage'));
const AchievementsPage = lazy(() => import('./ui/pages/AchievementsPage/AchievementsPage'));
const BreathworksPage = lazy(() => import('./ui/pages/BreathworksPage/BreathworksPage'));
const BreathworkDetailPage = lazy(() => import('./ui/pages/BreathworkDetailPage/BreathworkDetailPage'));
const ProfilePage = lazy(() => import('./ui/pages/ProfilePage/ProfilePage'));
const LoginPage = lazy(() => import('./ui/pages/LoginPage/LoginPage'));
const SignUpPage = lazy(() => import('./ui/pages/SignUpPage/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('./ui/pages/ForgotPasswordPage/ForgotPasswordPage'));
const AuthActionPage = lazy(() => import('./ui/pages/AuthActionPage/AuthActionPage'));
const PrivacyPolicyPage = lazy(() => import('./ui/pages/PrivacyPolicyPage/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./ui/pages/TermsOfServicePage/TermsOfServicePage'));
const BreathworkConfigurePage = lazy(() => import('./ui/pages/BreathworkConfigurePage/BreathworkConfigurePage'));
const BreathworkPlayerPage = lazy(() => import('./ui/pages/BreathworkPlayerPage/BreathworkPlayerPage'));
const BreathworkSuccessPage = lazy(() => import('./ui/pages/BreathworkSuccessPage/BreathworkSuccessPage'));
const ActivityDetailPage = lazy(() => import('./ui/pages/ActivityDetailPage').then(m => ({ default: m.ActivityDetailPage })));
const JourneyStepDetailPage = lazy(() => import('./ui/pages/JourneyStepDetailPage').then(m => ({ default: m.JourneyStepDetailPage })));
const AchievementDetailPage = lazy(() => import('./ui/pages/AchievementDetailPage').then(m => ({ default: m.AchievementDetailPage })));
const JourneysPage = lazy(() => import('./ui/pages/JourneysPage/JourneysPage'));
const JourneyDetailPage = lazy(() => import('./ui/pages/JourneyDetailPage/JourneyDetailPage'));
const WelcomePage = lazy(() => import('./ui/pages/WelcomePage/WelcomePage'));
const UpgradePage = lazy(() => import('./ui/pages/UpgradePage/UpgradePage'));
const ContactPage = lazy(() => import('./ui/pages/ContactPage/ContactPage'));
const PricingPage = lazy(() => import('./ui/pages/PricingPage/PricingPage'));
const DesignSystemIndex = lazy(() => import('./ui/pages/DesignSystem/DesignSystemIndex'));
const DesignSystemTypography = lazy(() => import('./ui/pages/DesignSystem/DesignSystemTypography'));
const DesignSystemColorPalette = lazy(() => import('./ui/pages/DesignSystem/DesignSystemColorPalette'));
const DesignSystemComponents = lazy(() => import('./ui/pages/DesignSystem/DesignSystemComponents'));

/** Renders the authenticated shell when logged in, otherwise the public layout. */
function AdaptiveLayout({ authShell: AuthShell }: { authShell: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [authTimedOut, setAuthTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => setAuthTimedOut(true), 3000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  if (isLoading && !authTimedOut) return null;
  if (isAuthenticated) return <AuthShell />;
  return <RootLayout />;
}

/**
 * Legal pages (Privacy Policy, Terms of Service) render chrome-less when
 * embedded in the mobile app's webview, which appends `?mobile=1` and supplies
 * its own native title bar. Outside the webview they use the standard adaptive
 * layout (authenticated shell when signed in, public navbar/footer otherwise).
 */
function LegalLayout({ authShell }: { authShell: React.ComponentType }) {
  const [searchParams] = useSearchParams();
  if (searchParams.has('mobile')) {
    return (
      <main className={legalLayoutStyles.bare}>
        <Outlet />
      </main>
    );
  }
  return <AdaptiveLayout authShell={authShell} />;
}

export default function App() {
  const layoutVariant = useLayoutVariant();
  const AuthenticatedShell = layoutVariant === 'navbar' ? AuthenticatedNavbarLayout : AuthenticatedLayout;

  const [consent, setConsent] = useState<CookieConsent | null>(getConsent);
  // The mobile app embeds legal pages in a webview (?mobile=1) with its own
  // chrome; don't render the cookie banner over that embedded view.
  const isMobileEmbed = new URLSearchParams(window.location.search).has('mobile');

  // Push the cookie choice into GA4 Consent Mode. 'all' grants cookie-backed
  // analytics; 'essential'/null stay denied (cookieless pings still flow). Also
  // boots the analytics instance so the first session ping fires at startup.
  useEffect(() => {
    applyAnalyticsConsent(consent);
  }, [consent]);

  // Tag the analytics user with the deployed web version (GA4 user property).
  // Sent on every session; Consent Mode governs whether it is cookie-backed.
  useEffect(() => {
    setUserProperty(AnalyticsUserProperty.webVersion, WEB_VERSION);
  }, []);

  // Tag subscription_status, signup_platform, last_platform (GA4 user props).
  // Reactive: re-fires when login state or subscription/trial resolves.
  const user = useAuth((s) => s.user);
  const { isPremium, isLoading: isSubscriptionLoading } = useSubscription();
  const { isInTrial } = useTrialState();
  useEffect(() => {
    const props = buildAnalyticsUserProperties({
      isLoggedIn: !!user,
      platform: 'web',
      subscription: { isPremium, isInTrial, isLoading: isSubscriptionLoading },
      signupPlatform: user?.signupPlatform ?? null,
    });
    props.forEach((p) => setUserProperty(p.name, p.value));
  }, [user, isPremium, isInTrial, isSubscriptionLoading]);

  // Fires trial_started (trial_type: 'auto_grant') exactly once when the
  // server-side no-card trial grant is first observed on the signed-in user.
  useTrialStartedEvent();

  function handleAcceptAll() {
    acceptAll();
    setConsent('all');
  }

  function handleAcceptEssential() {
    acceptEssential();
    setConsent('essential');
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <CookieBanner
        visible={consent === null && !isMobileEmbed}
        onAcceptAll={handleAcceptAll}
        onAcceptEssential={handleAcceptEssential}
      />
      <Suspense fallback={null}>
        <Routes>
          {/* Public routes — top navbar + footer */}
          <Route element={<RootLayout />}>
            <Route path={ROUTES.home} element={<ErrorBoundary fallback={<></>}><LandingPage /></ErrorBoundary>} />
            <Route path={ROUTES.login} element={<LoginPage />} />
            <Route path={ROUTES.signup} element={<SignUpPage />} />
            <Route path={ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
            <Route path={ROUTES.authAction} element={<AuthActionPage />} />
          </Route>

          {/* Legal pages — chrome-less in the mobile webview (?mobile=1), adaptive otherwise */}
          <Route element={<LegalLayout authShell={AuthenticatedShell} />}>
            <Route path={ROUTES.privacyPolicy} element={<PrivacyPolicyPage />} />
            <Route path={ROUTES.termsOfService} element={<TermsOfServicePage />} />
          </Route>

          {/* Shared routes — adaptive layout based on auth state */}
          <Route element={<AdaptiveLayout authShell={AuthenticatedShell} />}>
            <Route path={ROUTES.contact} element={<ContactPage />} />
            <Route path={ROUTES.pricing} element={<PricingPage />} />
            <Route path={ROUTES.breathworks} element={<BreathworksPage />} />
            <Route path={ROUTES.breathworkDetail} element={<BreathworkDetailPage />} />
            <Route path={ROUTES.journeys} element={<JourneysPage />} />
            <Route path={ROUTES.journeyDetail} element={<JourneyDetailPage />} />
          </Route>

          {/* Authenticated routes — sidebar or navbar depending on A/B variant */}
          <Route element={<ProtectedRoute><AuthenticatedShell /></ProtectedRoute>}>
            <Route path={ROUTES.loggedInHome} element={<HomePage />} />
            <Route path={ROUTES.breathworkConfigure} element={<BreathworkConfigurePage />} />
            <Route path={ROUTES.activity} element={<ActivityPage />} />
            <Route path={ROUTES.activityDetail} element={<ActivityDetailPage />} />
            <Route path={ROUTES.activitySessionDetail} element={<JourneyStepDetailPage />} />
            <Route path={ROUTES.achievements} element={<AchievementsPage />} />
            <Route path={ROUTES.achievementDetail} element={<AchievementDetailPage />} />
            <Route path={ROUTES.profile} element={<ProfilePage />} />
            <Route path={ROUTES.upgrade} element={<UpgradePage />} />
          </Route>

          <Route
            path={ROUTES.breathworkSuccess}
            element={<ProtectedRoute><BreathworkSuccessPage /></ProtectedRoute>}
          />

          <Route
            path={ROUTES.breathworkPlay}
            element={<ProtectedRoute><BreathworkPlayerPage /></ProtectedRoute>}
          />

          <Route
            path={ROUTES.welcome}
            element={<ProtectedRoute><WelcomePage /></ProtectedRoute>}
          />

          {/* Design system reference — dev only */}
          {import.meta.env.DEV && <>
            <Route path={ROUTES.designSystem} element={<DesignSystemIndex />} />
            <Route path={ROUTES.designSystemTypography} element={<DesignSystemTypography />} />
            <Route path={ROUTES.designSystemColorPalette} element={<DesignSystemColorPalette />} />
            <Route path={ROUTES.designSystemComponents} element={<DesignSystemComponents />} />
          </>}
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
